import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { Formik, Form as FormikForm } from "formik";
import { Save } from "lucide-react";
import * as yup from "yup";

import { updateProfile as updateProfileAPI } from "api/user";
import { EditUserType } from "api/user/types";
import FormikAutocomplete from "components/forms/FormikAutocomplete";
import FormikTextField from "components/forms/FormikTextField";
import { useUserStore } from "components/stores/UserStore";
import UltimateButton from "components/UltimateButton";

const ProfileForm = () => {
  const [user, setUser] = useUserStore((s) => [s.user, s.setUser]);

  const initialValues = {
    email: user?.email || "",
    name: user?.name || "",
    role: user?.role || null,
    phone: user?.phone || null,
  };

  const schema = yup.object({
    email: yup
      .string()
      .email("Wrong email format")
      .min(3, "Minimum 3 symbols")
      .max(50, "Maximum 50 symbols")
      .required("Email is required"),
    name: yup.string().required(),
  });

  const { mutateAsync: updateProfile, status } = useMutation(
    async (values: EditUserType) => {
      await updateProfileAPI(values);
    },
  );

  const handleSubmit = async (values: EditUserType) => {
    await updateProfile(values, {
      onSuccess: () => {
        if (user) setUser({ ...user, name: values.name, email: values.email });
      },
    });
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        <FormikForm>
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container columnSpacing={2} rowSpacing={3}>
              <Grid item xs={12} md={12}>
                <Typography variant="h5">Personal Info</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormikTextField name="name" label="Name" fullWidth />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormikAutocomplete
                  name="role"
                  label="Role"
                  options={[
                    {
                      value: "admin",
                      label: "Admin",
                    },
                    {
                      value: "partner",
                      label: "Referral Partner",
                    },
                    {
                      value: "partner-admin",
                      label: "Referral Partner Admin",
                    },
                    {
                      value: "partner-sales-rep",
                      label: "Referral Partner Sales Representative",
                    },
                  ]}
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={12}>
                <Typography variant="h5">Contact Info</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormikTextField
                  name="email"
                  label="Email"
                  fullWidth
                  disabled
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormikTextField
                  name="phone"
                  type="tel"
                  label="Phone"
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
          <Divider />
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={1}
            sx={{ p: 2 }}
          >
            <UltimateButton
              type="submit"
              text="Save"
              Icon={
                Save as React.ComponentType<{
                  size?: number;
                  className?: string;
                  style?: React.CSSProperties;
                }>
              }
              loading={status === "loading"}
              sx={{
                minWidth: 150,
                py: 1.5,
              }}
            />
          </Stack>
        </FormikForm>
      </Formik>
    </>
  );
};

export default ProfileForm;
